
import React, { FC, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { FaCheckCircle, FaExclamationTriangle, FaTimesCircle } from 'react-icons/fa';
import { FiTrash } from 'react-icons/fi';
import ButtonComponent from 'app_components/commons/Button/ButtonComponent';
import TextComponent from 'app_components/commons/TextComponent/TextComponent';
import AlertComponent from 'app_components/commons/alert';
import { CombinedReducers } from 'app_events/index';
import { shoppingCarChange } from 'app_events/app/actions';
import { IObjectToBuy, IShoppingCarItem, PROCESS_ITEM_PURCHASE } from 'app_types/shoppingCart/shoppingCarTypes';
import './ShoppingCart.scss';

interface IModalReviewProps {
  open: boolean;
  onClose: (force: boolean, updatedCart?: IShoppingCarItem[]) => void;
  data: Record<string, IObjectToBuy>;
}

const ModalReviewPurchase: FC<IModalReviewProps> = ({ open, onClose, data }) => {
  const dispatch = useDispatch();
  const [reviewData, setReviewData] = useState<Record<string, IObjectToBuy>>({});
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [canContinue, setCanContinue] = useState<boolean>(true);
  const [hasRestrictedItems, setHasRestrictedItems] = useState<boolean>(false);
  const { data: shoppingCarItems } = useSelector((store: CombinedReducers) =>
    store?.app?.shoppingCart ?? { action: 'none', data: [] }
  );

  // Procesar los datos al montar el componente
  useEffect(() => {
    processReviewData();
  }, [data]);

  const processReviewData = () => {
    const processedData = { ...data };
    let hasRestricted = false;

    // Marcar los elementos restringidos
    Object.keys(processedData).forEach(key => {
      if (processedData[key].processPurchase === PROCESS_ITEM_PURCHASE.RESTRICTED) {
        hasRestricted = true;
      }
    });

    setReviewData(processedData);
    setHasRestrictedItems(hasRestricted);

    // Verificar si todos los elementos están restringidos
    const allRestricted = Object.keys(processedData).every(
      key => processedData[key].processPurchase === PROCESS_ITEM_PURCHASE.RESTRICTED
    );

    setCanContinue(!allRestricted && shoppingCarItems.length > 0);
  };

  const handleRemoveItem = (item: IObjectToBuy) => {
    // Actualizar el estado local
    const updatedReviewData = { ...reviewData };
    updatedReviewData[item.identifier].removed = true;
    setReviewData(updatedReviewData);

    // Verificar si podemos continuar (si hay elementos no eliminados)
    const hasAvailableItems = Object.keys(updatedReviewData).some(key =>
      !updatedReviewData[key].removed &&
      updatedReviewData[key].processPurchase !== PROCESS_ITEM_PURCHASE.RESTRICTED
    );

    setCanContinue(hasAvailableItems);

    // Si todos los elementos están marcados como eliminados, podemos cerrar el modal
    const allRemoved = Object.keys(updatedReviewData).every(key =>
      updatedReviewData[key].removed
    );

    if (allRemoved) {
      onClose(false);
    }
  };

  const handleContinue = async () => {
    setErrorMessage(undefined);

    // Obtener los IDs de los elementos marcados como eliminados
    const removedItemIds = Object.keys(reviewData)
    .filter(key => reviewData[key].removed)
    .map(key => reviewData[key].identifier);

    // Verificar si hay elementos restringidos no eliminados en el carrito
    const restrictedInCart = Object.keys(reviewData)
      .filter(key =>
        reviewData[key].processPurchase === PROCESS_ITEM_PURCHASE.RESTRICTED &&
        !reviewData[key].removed
      );

    const cartItemIds = shoppingCarItems.map(item => item.identifier);
    const hasRestrictedInCart = restrictedInCart.some(id => cartItemIds.includes(id));

    if (hasRestrictedInCart) {
      setErrorMessage('No puedes continuar con elementos restringidos en tu carrito. Por favor, revisa y elimina los elementos marcados.');
      return;
    }

    // Crear una versión filtrada del carrito excluyendo los elementos marcados como eliminados
    const updatedCart = shoppingCarItems.filter(
      cartItem => cartItem && !removedItemIds.includes(cartItem.identifier)
    );

    if (updatedCart.length === 0) {
      setErrorMessage('No hay elementos disponibles para comprar en tu carrito.');
      return;
    }
    // Actualizar el carrito en Redux
    if (removedItemIds.length > 0) {
      dispatch(shoppingCarChange({ action: 'remove', data: updatedCart }));
    }

    // En lugar de esperar a que Redux actualice el estado, pasamos directamente
    // el carrito actualizado al cerrar el modal
    onClose(true, updatedCart);
  };

  const getStatusIcon = (item: IObjectToBuy) => {
    switch (item.processPurchase) {
      case PROCESS_ITEM_PURCHASE.AVAILABLE:
        return <FaCheckCircle className="status-icon available" />;
      case PROCESS_ITEM_PURCHASE.WARNING:
        return <FaExclamationTriangle className="status-icon warning" />;
      case PROCESS_ITEM_PURCHASE.RESTRICTED:
        return <FaTimesCircle className="status-icon restricted" />;
      default:
        return null;
    }
  };

  const getStatusClass = (item: IObjectToBuy) => {
    switch (item.processPurchase) {
      case PROCESS_ITEM_PURCHASE.AVAILABLE:
        return 'status-available';
      case PROCESS_ITEM_PURCHASE.WARNING:
        return 'status-warning';
      case PROCESS_ITEM_PURCHASE.RESTRICTED:
        return 'status-restricted';
      default:
        return '';
    }
  };

  const getStatusLabel = (item: IObjectToBuy) => {
    switch (item.processPurchase) {
      case PROCESS_ITEM_PURCHASE.AVAILABLE:
        return 'Disponible';
      case PROCESS_ITEM_PURCHASE.WARNING:
        return 'Advertencia';
      case PROCESS_ITEM_PURCHASE.RESTRICTED:
        return 'Restringido';
      default:
        return '';
    }
  };

  return (
    <Modal
      show={open}
      onHide={() => onClose(false)}
      size="lg"
      centered
      className="review-purchase-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Revisión de tu compra</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="review-purchase-content">
          <TextComponent type="paragraph" className="review-intro-text">
            {hasRestrictedItems
              ? 'Algunos elementos de tu carrito no están disponibles para compra. Revisa los detalles a continuación.'
              : 'Revisa los elementos de tu carrito antes de continuar con la compra.'}
          </TextComponent>

          {errorMessage && (
            <AlertComponent
              description={errorMessage}
              type="error"
              className="review-error-alert"
            />
          )}

          <div className="review-items-container">
            {Object.keys(reviewData).length === 0 ? (
              <div className="no-items-message">
                No hay elementos para revisar
              </div>
            ) : (
              Object.keys(reviewData).map((key) => {
                const item = reviewData[key];
                return (
                  <div
                    key={item.identifier}
                    className={`review-item ${getStatusClass(item)} ${item.removed ? 'removed' : ''}`}
                  >
                    <div className="review-item-status">
                      {getStatusIcon(item)}
                    </div>

                    <div className="review-item-content">
                      <div className="review-item-header">
                        <h4 className="review-item-title">
                          {item.programName}
                        </h4>
                        <span className={`review-item-badge ${getStatusClass(item)}`}>
                          {getStatusLabel(item)}
                        </span>
                      </div>

                      <p className="review-item-reason">
                        {item.reason}
                      </p>

                      <div className="review-item-details">
                        <span>Código: {item.programCode}</span>
                      </div>
                    </div>

                    <div className="review-item-actions">
                      {[PROCESS_ITEM_PURCHASE.RESTRICTED, PROCESS_ITEM_PURCHASE.WARNING].includes(item.processPurchase) && !item.removed && (
                        <ButtonComponent
                          variant="tertiary"
                          onClick={() => handleRemoveItem(item)}
                          className="remove-item-btn"
                        >
                          <FiTrash /> Eliminar
                        </ButtonComponent>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <ButtonComponent
          onClick={() => onClose(false)}
          variant="tertiary"
          className="cancel-btn"
        >
          Cancelar
        </ButtonComponent>

        {canContinue && (
          <ButtonComponent
            onClick={handleContinue}
            variant="primary"
            className="continue-btn"
          >
            Continuar con la compra
          </ButtonComponent>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ModalReviewPurchase;